import { inject, Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { Observable, of, switchMap, tap } from 'rxjs';
import { User } from '../../../domain/user/model/user';
import { UserPageItem } from '../../../domain/user/model/user-page-item';
import { UserPageQuery } from '../../../domain/user/model/user-page-query';
import { UserApiService } from '../../../domain/user/service/user-api.service';
import { Page } from '../../../shared/model/local/pagination/page';
import { isDataCached } from '../../../shared/helper/is-data-cached';
import { CacheDataActions } from '../../../shared/store/cache-data/cache-data.actions';
import { CacheDataState } from '../../../shared/store/cache-data/cache-data.state';
import { UserActions } from './user.actions';
import { UserPageCriteriaDto } from '../model/user-page-criteria-dto';

export interface UserStateModel {
  user: Record<string, User>;
  [key: string]: unknown;
}

const defaults: UserStateModel = {
  user: {},
};

@State<UserStateModel>({
  name: 'user',
  defaults,
})
@Injectable()
export class UserState {
  private static readonly userListCacheKey = 'user-list';

  private readonly store = inject(Store);
  private readonly userApiService = inject(UserApiService);

  static page() {
    return createSelector(
      [CacheDataState.getDataByKey(UserState.userListCacheKey)],
      (data: unknown) => data as Page<UserPageItem> | undefined
    );
  }

  static criteria() {
    return createSelector(
      [CacheDataState.getCriteriaByKey(UserState.userListCacheKey)],
      (data: unknown) => data as UserPageCriteriaDto | undefined
    );
  }

  static user(id: string) {
    return createSelector(
      [UserState],
      (state: UserStateModel) => state.user?.[id] as User | undefined
    );
  }

  /** The grid's `"firstName,lastName asc"` becomes `["firstName asc", "lastName asc"]`. */
  static toPageQuery(criteria: UserPageCriteriaDto): UserPageQuery {
    const sort = criteria.sortOrders?.trim();
    let sortOrders: string[] | undefined;
    if (sort) {
      const lastSpace = sort.lastIndexOf(' ');
      const direction = sort.slice(lastSpace + 1).toLowerCase();
      sortOrders = sort
        .slice(0, lastSpace)
        .split(',')
        .map(path => path.trim())
        .filter(Boolean)
        .map(path => `${path} ${direction}`);
    }
    return {
      pageNumber: criteria.pageNumber,
      pageSize: criteria.pageSize,
      sortOrders,
      userTypes: criteria.userTypes,
      enabled: criteria.enabled,
      searchPhrase: criteria.searchPhrase,
    };
  }

  @Action(UserActions.GetById)
  getById(
    ctx: StateContext<UserStateModel>,
    { id }: UserActions.GetById
  ): Observable<User> {
    return this.userApiService
      .getById(id)
      .pipe(tap(user => this.patchUser(ctx, user)));
  }

  @Action(UserActions.GetPage, { cancelUncompleted: true })
  getPage(
    ctx: StateContext<UserStateModel>,
    { criteria }: UserActions.GetPage
  ): Observable<unknown> {
    if (isDataCached(UserState.userListCacheKey, criteria, 1000 * 5)) {
      return this.fetchPageData(criteria);
    }
    return of(null);
  }

  @Action(UserActions.RefreshPage, { cancelUncompleted: true })
  refreshPage(): Observable<unknown> {
    return this.fetchPageData();
  }

  @Action(UserActions.InvalidateCache)
  invalidateCache(ctx: StateContext<UserStateModel>) {
    return ctx.dispatch(
      new CacheDataActions.InvalidateCache(UserState.userListCacheKey)
    );
  }

  @Action(UserActions.Create)
  create(
    ctx: StateContext<UserStateModel>,
    { user }: UserActions.Create
  ): Observable<unknown> {
    return this.userApiService.create(user).pipe(
      tap(created => this.patchUser(ctx, created)),
      switchMap(() => ctx.dispatch(new UserActions.InvalidateCache()))
    );
  }

  @Action(UserActions.Update)
  update(
    ctx: StateContext<UserStateModel>,
    { id, user }: UserActions.Update
  ): Observable<unknown> {
    return this.userApiService.update(id, user).pipe(
      tap(updated => this.patchUser(ctx, updated)),
      switchMap(() => ctx.dispatch(new UserActions.InvalidateCache()))
    );
  }

  @Action(UserActions.Disable)
  disable(
    ctx: StateContext<UserStateModel>,
    { id }: UserActions.Disable
  ): Observable<unknown> {
    return this.userApiService
      .disable(id)
      .pipe(switchMap(() => this.reload(ctx, id)));
  }

  @Action(UserActions.Enable)
  enable(
    ctx: StateContext<UserStateModel>,
    { id }: UserActions.Enable
  ): Observable<unknown> {
    return this.userApiService
      .enable(id)
      .pipe(switchMap(() => this.reload(ctx, id)));
  }

  @Action(UserActions.ResendInvitation)
  resendInvitation(
    _ctx: StateContext<UserStateModel>,
    { id }: UserActions.ResendInvitation
  ): Observable<void> {
    return this.userApiService.resendInvitation(id);
  }

  private reload(
    ctx: StateContext<UserStateModel>,
    id: string
  ): Observable<unknown> {
    return ctx
      .dispatch(new UserActions.InvalidateCache())
      .pipe(switchMap(() => ctx.dispatch(new UserActions.GetById(id))));
  }

  private patchUser(ctx: StateContext<UserStateModel>, user: User): void {
    ctx.patchState({
      user: {
        ...ctx.getState().user,
        [user.id]: user,
      },
    });
  }

  private fetchPageData(criteria?: UserPageCriteriaDto): Observable<unknown> {
    const pageCriteria =
      criteria ??
      (this.store.selectSnapshot(
        CacheDataState.getCriteriaByKey(UserState.userListCacheKey)
      ) as UserPageCriteriaDto | undefined) ??
      new UserPageCriteriaDto();

    return this.userApiService
      .getPage(UserState.toPageQuery(pageCriteria))
      .pipe(
        switchMap(page =>
          this.store.dispatch(
            new CacheDataActions.SetCachedData(
              UserState.userListCacheKey,
              page,
              pageCriteria
            )
          )
        )
      );
  }
}
